import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import Chart from 'chart.js/auto';
import { BuildingData } from '../../../../core/interfaces/solar-interfaces';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest } from 'rxjs';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'app-return-on-investment-chart',
  standalone: true,
  imports: [ChartModule],
  providers: [DecimalPipe],
  templateUrl: './return-on-investment-chart.component.html',
  styleUrl: './return-on-investment-chart.component.css'
})
export class ReturnOnInvestmentChartComponent {
  @Input() initialInvestment: number | null = null;
  @Input() yearlyReturn: number | null = null;
  @Input() roiTimeFrame: number | null = 25;

  data: any;
  options: any;

  cumulativeReturnLabel: string = "Cumulative Return";
  initialInvestmentLabel: string = "Initial Investment";
  yearsLabel: string = "Years"

  constructor(private translocoService: TranslocoService, private decimalPipe: DecimalPipe) { }

  ngOnInit(): void {
    this.updateChart();
    combineLatest([
      this.translocoService.selectTranslate('services.cumulative_return'),
      this.translocoService.selectTranslate('services.initial_investment'),
      this.translocoService.selectTranslate('services.years'),
    ]).subscribe(([crL, iiL, yL]) => {
      this.cumulativeReturnLabel = crL;
      this.initialInvestmentLabel = iiL;
      this.yearsLabel = yL;
      this.updateChart();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateChart();
  }

  updateChart(): void {
    if (this.initialInvestment === null || this.yearlyReturn === null || this.roiTimeFrame === null) {
      return;
    }

    const years = Array.from({ length: this.roiTimeFrame + 1 }, (_, i) => i);
    const cumulativeReturn = years.map(year => year * this.yearlyReturn!);
    const initialInvestmentLine = Array(years.length).fill(this.initialInvestment);

    this.data = {
      labels: years,
      datasets: [
        {
          label: this.cumulativeReturnLabel,
          data: cumulativeReturn,
          borderColor: '#a9be92',
          backgroundColor: '#a9be92',
          tension: 0.1,
          borderWidth: 2,
          fill: false,
          pointRadius: 0,
        },
        {
          label: this.initialInvestmentLabel,
          data: initialInvestmentLine,
          borderColor: '#80a7a5',
          backgroundColor: '#80a7a5',
          borderWidth: 2,
          fill: false,
          pointRadius: 0,
        }
      ]
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        datalabels: {
          display: false
        },
        legend: {
          display: true,
          position: 'top'
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const index = context.dataIndex;
              let label = context.dataset.label || '';
              let value = context.raw || 0;
              return `${label}: ${this.decimalPipe.transform(value, '1.2-2') || value}`;
            }
          }
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: this.yearsLabel
          }
        },
        y: {
          title: {
            display: true,
            text: this.cumulativeReturnLabel
          },
          beginAtZero: true,
          ticks: {
            callback: function (value: number) {
              return '€' + (value / 1000).toFixed(0) + 'k';
            }
          }
        }
      }
    }
  }
}
